import React, { useCallback, useEffect, useState } from 'react';
import Iconify from 'src/components/Iconify';
import propostasService from 'src/services/propostasService';
import { PROPOSTAS_DESIGN as D } from './Propostas.design';

// Como o cliente interagiu com o link público: visitas, tempo de leitura,
// downloads do PDF, resposta e assinatura. Usado no drawer da lista/kanban e
// dentro da sidebar do editor — por isso não traz cabeçalho próprio.

function formatarDuracao(segundos) {
  const total = Math.max(0, Math.round(segundos || 0));
  if (total < 60) return `${total}s`;
  const minutos = Math.floor(total / 60);
  if (minutos < 60) return `${minutos}m ${total % 60}s`;
  return `${Math.floor(minutos / 60)}h ${minutos % 60}m`;
}

function formatarDataHora(valor) {
  if (!valor) return '—';
  return new Date(valor).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const EVENTOS = {
  view: { label: 'Abriu a proposta', icon: 'mdi:eye-outline', classe: 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  pdf_download: { label: 'Baixou o PDF', icon: 'mdi:download-outline', classe: 'bg-gray-100 dark:bg-[#383838] text-gray-600 dark:text-[#ABABAD]' },
  sign: { label: 'Assinou a proposta', icon: 'mdi:draw-pen', classe: 'bg-violet-50 dark:bg-violet-500/10 text-[#7C3AED]' },
  accept: { label: 'Aceitou a proposta', icon: 'mdi:check-circle-outline', classe: 'bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400' },
  decline: { label: 'Recusou a proposta', icon: 'mdi:close-circle-outline', classe: 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400' }
};

function AssinaturaCard({ assinatura, pdfAssinadoUrl }) {
  const linhas = [
    ['Assinante', assinatura.nome],
    ['Documento', assinatura.documento],
    ['E-mail', assinatura.email],
    ['Data e hora', formatarDataHora(assinatura.assinado_em)],
    ['IP', assinatura.ip],
    ['Dispositivo', assinatura.dispositivo]
  ].filter(([, valor]) => Boolean(valor));

  return (
    <div className={D.assinatura.card}>
      <div className={D.assinatura.header}>
        <div className={D.assinatura.headerIcon}>
          <Iconify icon="mdi:draw-pen" width={15} />
        </div>
        <div className="min-w-0">
          <p className={D.assinatura.headerTitle}>Assinada pelo cliente</p>
          <p className={D.assinatura.headerSub}>{formatarDataHora(assinatura.assinado_em)}</p>
        </div>
      </div>

      {assinatura.imagem_url && (
        <div className={D.assinatura.imagemBox}>
          <img src={assinatura.imagem_url} alt="Assinatura do cliente" className={D.assinatura.imagem} />
        </div>
      )}

      <div className="mt-3">
        {linhas.map(([label, valor]) => (
          <div key={label} className={D.assinatura.linha}>
            <span className={D.assinatura.linhaLabel}>{label}</span>
            <span className={D.assinatura.linhaValor}>{valor}</span>
          </div>
        ))}
      </div>

      {assinatura.hash_documento && (
        <div className="mt-2">
          <span className={D.acompanhamento.sectionLabel}>Hash do documento (SHA-256)</span>
          <p className={D.assinatura.hash}>{assinatura.hash_documento}</p>
        </div>
      )}

      {pdfAssinadoUrl ? (
        <a href={pdfAssinadoUrl} target="_blank" rel="noopener noreferrer" className={D.assinatura.download}>
          <Iconify icon="mdi:file-pdf-box" width={14} />
          Baixar PDF assinado
        </a>
      ) : (
        <p className="text-[10px] text-gray-400 mt-3 text-center">
          O PDF com o certificado está sendo gerado.
        </p>
      )}
    </div>
  );
}

export default function AcompanhamentoPanel({ propostaId }) {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  const carregar = useCallback(async () => {
    if (!propostaId) return;
    setLoading(true);
    setErro(null);
    try {
      const resposta = await propostasService.eventos(propostaId);
      setDados(resposta.data);
    } catch (err) {
      setErro('Não foi possível carregar o acompanhamento');
    } finally {
      setLoading(false);
    }
  }, [propostaId]);

  useEffect(() => { carregar(); }, [carregar]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-gray-400">
        <Iconify icon="mdi:loading" width={20} className="animate-spin text-[#7C3AED]" />
      </div>
    );
  }

  if (erro) {
    return (
      <div className={D.acompanhamento.vazio}>
        <Iconify icon="mdi:alert-circle-outline" width={22} className="text-gray-300" />
        <p className={D.acompanhamento.vazioTexto}>{erro}</p>
        <button type="button" onClick={carregar} className="text-[11px] font-medium text-[#7C3AED] hover:underline mt-2">
          Tentar novamente
        </button>
      </div>
    );
  }

  const resumo = dados?.resumo || {};
  const eventos = dados?.eventos || [];

  return (
    <div className={D.acompanhamento.wrapper}>
      <div className={D.acompanhamento.statsGrid}>
        <div className={D.acompanhamento.statCard}>
          <p className={D.acompanhamento.statValue}>{resumo.visitas || 0}</p>
          <p className={D.acompanhamento.statLabel}>Aberturas</p>
        </div>
        <div className={D.acompanhamento.statCard}>
          <p className={D.acompanhamento.statValue}>{formatarDuracao(resumo.tempo_total_seg)}</p>
          <p className={D.acompanhamento.statLabel}>Tempo total</p>
        </div>
        <div className={D.acompanhamento.statCard}>
          <p className={D.acompanhamento.statValue}>{formatarDuracao(resumo.tempo_medio_seg)}</p>
          <p className={D.acompanhamento.statLabel}>Média por visita</p>
        </div>
        <div className={D.acompanhamento.statCard}>
          <p className={D.acompanhamento.statValue}>{Math.round(resumo.scroll_max_percent || 0)}%</p>
          <p className={D.acompanhamento.statLabel}>Leitura máx.</p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 text-[11px] text-gray-500 dark:text-[#ABABAD] px-0.5">
        <span>1ª abertura: <strong className="font-medium text-gray-700 dark:text-[#D1D2D3]">{formatarDataHora(resumo.primeira_em)}</strong></span>
        <span>{resumo.downloads || 0} download{(resumo.downloads || 0) === 1 ? '' : 's'}</span>
      </div>

      {dados?.assinatura_cliente && (
        <AssinaturaCard assinatura={dados.assinatura_cliente} pdfAssinadoUrl={dados.pdf_assinado_url} />
      )}

      <div>
        <span className={D.acompanhamento.sectionLabel}>Linha do tempo</span>
        {eventos.length === 0 ? (
          <div className={D.acompanhamento.vazio}>
            <Iconify icon="mdi:timeline-clock-outline" width={24} className="text-gray-300" />
            <p className={D.acompanhamento.vazioTexto}>O cliente ainda não abriu esta proposta</p>
          </div>
        ) : (
          <div className={D.acompanhamento.timeline}>
            {eventos.map(evento => {
              const cfg = EVENTOS[evento.tipo] || EVENTOS.view;
              // Só visitas têm duração/dispositivo; os demais eventos são pontuais.
              const detalhes = [
                formatarDataHora(evento.created_at),
                evento.tipo === 'view' && evento.duracao_seg ? formatarDuracao(evento.duracao_seg) : null,
                evento.tipo === 'view' && evento.tempo_ativo_seg ? `${formatarDuracao(evento.tempo_ativo_seg)} de leitura` : null,
                evento.tipo === 'view' && evento.scroll_max_percent ? `${Math.round(evento.scroll_max_percent)}% lido` : null,
                [evento.dispositivo, evento.navegador, evento.os].filter(Boolean).join(' · ') || null
              ].filter(Boolean);

              return (
                <div key={evento._id} className={D.acompanhamento.evento}>
                  <div className={`${D.acompanhamento.eventoIcone} ${cfg.classe}`}>
                    <Iconify icon={cfg.icon} width={14} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={D.acompanhamento.eventoTitulo}>{cfg.label}</p>
                    <p className={D.acompanhamento.eventoMeta}>{detalhes.join(' · ')}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
